import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Checkbox,
  Text,
  useToast,
  Input,
  FormHelperText,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
// No need for external date utils, we'll format dates inline

/**
 * Modal shown when completing a task
 * Allows adding comments, updating lead follow-up date, and creating another task
 */
const TaskCompletionModal = ({ 
  isOpen, 
  onClose, 
  task, 
  onComplete,
  currentFollowUp = null 
}) => {
  const [comment, setComment] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [createAnotherTask, setCreateAnotherTask] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  // Get ISO string from date for datetime-local input
  const getLocalDateTimeString = (date) => {
    const d = date ? new Date(date) : new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  
  // Format dates for display
  const formattedCurrentFollowUp = currentFollowUp ? 
    new Date(currentFollowUp).toLocaleString() : 'None set';
  
  // Function to set preset follow-up times
  const setFollowUpPreset = (preset) => {
    const now = new Date();
    let followUpDate = new Date(now);
    
    switch(preset) {
      case '2hours':
        followUpDate.setHours(now.getHours() + 2);
        break;
      case '6hours':
        followUpDate.setHours(now.getHours() + 6);
        break;
      case '1day':
        followUpDate.setDate(now.getDate() + 1);
        break;
      case '3days':
        followUpDate.setDate(now.getDate() + 3);
        break;
      case '5days':
        followUpDate.setDate(now.getDate() + 5);
        break;
      case '7days':
        followUpDate.setDate(now.getDate() + 7);
        break;
      default:
        // Default to now
        break;
    }
    
    setFollowUpDate(getLocalDateTimeString(followUpDate));
  };
  
  // Initialize follow-up date with current value when modal opens
  useEffect(() => {
    if (isOpen) {
      if (currentFollowUp) {
        setFollowUpDate(getLocalDateTimeString(new Date(currentFollowUp)));
      } else {
        setFollowUpDate('');
      }
    }
  }, [isOpen, currentFollowUp]);
  
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Call the parent handler with all the collected information
      await onComplete(task._id, 'completed', {
        comment,
        followUpDate: followUpDate || null,
        createAnotherTask
      });
      
      // Reset form state
      setComment('');
      setFollowUpDate('');
      setCreateAnotherTask(false);
      
      onClose();
    } catch (error) {
      toast({
        title: 'Error completing task',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Complete Task</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box bg="blue.50" p={2} borderRadius="md">
              <Text fontWeight="medium">Task: {task?.title}</Text>
              {task?.leadDetails && (
                <Text fontSize="sm" color="gray.600">
                  Lead: {task.leadDetails.brideAndGroomName || 
                          (task.leadDetails.firstName && task.leadDetails.lastName ? 
                          `${task.leadDetails.firstName} ${task.leadDetails.lastName}` : 
                          (task.leadDetails.email || 'Unknown'))}
                </Text>
              )}
            </Box>
            
            <FormControl>
              <FormLabel>
                Add Comment <Text as="span" fontSize="sm" color="gray.500" fontWeight="normal">(Optional)</Text>
              </FormLabel>
              <Textarea 
                placeholder="Add notes about the completed task..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                borderColor="gray.200"
              />
              <FormHelperText color="gray.500">Add any notes about this completed task</FormHelperText>
            </FormControl>
            
            <FormControl>
              <FormLabel>
                Update Next Follow-Up Date and Time <Text as="span" fontSize="sm" color="gray.500" fontWeight="normal">(Optional)</Text>
              </FormLabel>
              <HStack spacing={2}>
                <Input 
                  type="datetime-local"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  flex="1"
                  borderColor="gray.200"
                />
                <Menu>
                  <MenuButton 
                    as={Button} 
                    size="sm" 
                    rightIcon={<ChevronDownIcon />}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Presets
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setFollowUpPreset('2hours')}>In 2 hours</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('6hours')}>In 6 hours</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('1day')}>Tomorrow</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('3days')}>In 3 days</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('5days')}>In 5 days</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('7days')}>In 1 week</MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              <FormHelperText color="gray.500">
                {currentFollowUp ? 
                  <Text fontSize="sm">Current follow-up: <b>{formattedCurrentFollowUp}</b></Text> : 
                  'Set a new follow-up date and time for this lead'}
              </FormHelperText>
            </FormControl>
            
            <Checkbox 
              isChecked={createAnotherTask}
              onChange={(e) => setCreateAnotherTask(e.target.checked)}
              colorScheme="blue"
              size="md"
            >
              Create another task for this lead
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="outline" onClick={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Completing..."
            >
              Mark as Completed
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaskCompletionModal;
